import { RouteProvider } from "views/components/Provider";
import { dedup, pathOr } from "lib/functions";
import { filterLines, selectVisibleLines } from "state/lines/selectors";
import {
	isDniRequired as isDniRequiredFn,
	isRutRequired as isRutRequiredFn,
} from "lib/helpers/documents";
import { or } from "ramda";
import { useAuth } from "hooks/useAuth";
import { useEffect } from "react";
import { useGlobal, useState } from "reactn";
import { useLinesByOfficeSlug } from "state/lines/hooks";
import { useOffice } from "state/offices/hooks";
import { validatePhone } from "lib/rut";

const reservationSteps = {
	calendar: "CALENDAR",
	showLines: "SHOW_LINES",
	confirmReservation: "CONFIRM_RESERVATION",
};

const OfficeProvider = ({ match, history, location, children }) => {
	const officeSlug = match.params.slug;
	const [user] = useGlobal("user");
	const [categories] = useGlobal("categories");
	const [config] = useGlobal("UIConfig");
	const [office, officeLoading] = useOffice(officeSlug);
	const { isLoggedInTemporary, isLoggedIn } = useAuth();
	const [linesLoading, linesNew] = useLinesByOfficeSlug(officeSlug);
	const linesObj = pathOr(false, ["lines"], office);
	const lines = linesObj ? Object.values(linesObj) : linesNew;
	const hasDni = user && user.dni && user.dni.length > 0 ? true : false;
	const modeRut = pathOr(false, ["options", "mode_rut"], office);
	const modeDni = pathOr(false, ["options", "dni_peru"], office);
	const hasRut = user && user.rut && user.rut.length > 0 ? true : false;
	const rutOrDni = or(hasRut ? true : false, hasDni ? true : false);
	const [wantTemporary, setWantTemporary] = useState(false);
	const [isRutUpdating, setIsRutUpdating] = useState(false);
	const [isRutModalVisible, setIsRutModalVisible] = useState(false);
	const [temporaryRut, setTemporaryRut] = useState("");
	const [temporaryDni, setTemporaryDni] = useState("");
	const [openRutUpdate, setOpenRutUpdate] = useState(false);

	const reservationsOptions = pathOr(
		"unexist",
		["options", "reservations", "fields"],
		office
	);

	const isDniRequired = isDniRequiredFn({
		hasDni,
		isLoggedIn,
		isLoggedInTemporary,
		modeDni,
		modeRut,
	});

	const isRutRequired = isRutRequiredFn({
		hasRut,
		isLoggedIn,
		isLoggedInTemporary,
		modeRut,
		modeDni,
	});

	const documentModalType = isDniRequired ? "dni" : "rut";

	const [state, setState] = useState({
		dateSelected: null,
		step: reservationSteps.calendar,
		lineFilters: [],
		blocksByDay: {},
		lineSelected: {},
		blockSelected: {},
		linesByFolder: [],
	});
	const [loginModalState, setLoginModalState] = useState("");
	const [registerModalState, setRegisterModalState] = useState("");
	const [modalWithoutLoginState, setModalWithoutLoginState] =
		useState("closed");
	const [phoneRequired, setPhoneRequired] = useState(null);
	const [isUnexistRequiredField, setIsUnexistRequiredField] = useState(false);
	const [isVisibleConfirmationModal, setIsVisibleConfirmationModal] =
		useState(false);

	const {
		dateSelected,
		step,
		lineFilters,
		lineSelected,
		blocksByDay,
		blockSelected,
		linesByFolder,
	} = state;

	const { hideOfficeDetailText } = config;

	const folder = new URLSearchParams(location.search).get("folder");

	const showKioskLines = pathOr(false, ["options", "showKioskLines"], office);

	const category = office ? categories.byId[office.category_id] : null;
	const hasPhone =
		user && user.phone && user.phone.length > 0 && validatePhone(user.phone)
			? true
			: false;

	const referrer = folder
		? location.pathname
		: `/search?category=${category && category.slug}`;

	const historyBack = () => history.push(referrer);

	const navToFolder = (folder) => () =>
		history.push(
			location.pathname + `?folder=${encodeURIComponent(folder)}`
		);

	const visibleLines =
		lines &&
		lines.length &&
		selectVisibleLines(lines)
			.filter((l) =>
				folder ? l.folder === decodeURIComponent(folder) : !l.folder
			)
			.filter((l) => l.type !== "urgent");

	const filteredKioskLines = showKioskLines
		? visibleLines
		: filterLines(visibleLines);

	useEffect(() => {
		if (folder && !linesByFolder.length) {
			setState((prevState) => ({
				...prevState,
				linesByFolder: filteredKioskLines.filter(
					(item) => item.folder === folder
				),
			}));
		} else {
			setState((prevState) => ({
				...prevState,
				linesByFolder: [],
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [folder]);

	const ticketAction = () => {
		if (user.guest) {
			isActiveLoginModal();
		}
	};

	const loginModalIsVisible = loginModalState === "open";

	const closeLoginModal = () => {
		setLoginModalState("closed");
	};

	const isActiveLoginModal = () => {
		if (user.guest) {
			setLoginModalState("open");
		} else {
			ticketAction();
		}
	};

	const activeSignUpModalInTemporaryUser = () => {
		setRegisterModalState("open");
	};
	const signUpModalIsVisible = registerModalState === "open";
	const isActiveRegistryModal = () => {
		if (loginModalState === "open") {
			setLoginModalState("close");
			setRegisterModalState("open");
		} else {
			ticketAction();
		}
	};
	const closeSignUpModal = () => setRegisterModalState("closed");

	const handlerWithoutLoginModal = () => {
		closeSignUpModal();
		closeLoginModal();
		setModalWithoutLoginState("open");
	};

	const closeReserveWihtoutLogin = () => {
		setModalWithoutLoginState("closed");
	};
	const isVisibleModalWithoutLogin =
		loginModalState === "closed" && modalWithoutLoginState === "open";

	const handleOpenUpdateRutModal = () => {
		if (rutOrDni && phoneRequired && !phoneRequired.required) {
			return false;
		}

		if (isUnexistRequiredField) {
			return false;
		}
		if (rutOrDni && hasPhone) {
			return false;
		}
		return true;
	};

	const folders = selectVisibleLines(lines)
		.map((l) => l.folder)
		.filter((f) => f)
		.filter(dedup);

	const handleOnSelectDate = (date) => {
		setState((prevState) => ({ ...prevState, dateSelected: date }));
	};

	const handleOnContinueReservationSteps = ({ blocks, line }) => {
		if (step === reservationSteps.calendar && dateSelected) {
			const lineSlugs = Object.keys(blocks);
			const filterLines = lines.filter(
				(line) =>
					lineSlugs.includes(line.slug) && line.folder === folder
			);

			setState((prevState) => ({
				...prevState,
				step: reservationSteps.showLines,
				lineFilters: filterLines,
				blocksByDay: blocks,
			}));
		}

		if (step === reservationSteps.showLines) {
			setState((prevState) => ({
				...prevState,
				step: reservationSteps.confirmReservation,
				lineSelected: line,
				blockSelected: blocksByDay[line.slug],
			}));
		}
	};

	const handleOnBack = () => {
		if (
			step === reservationSteps.confirmReservation ||
			step === reservationSteps.showLines
		) {
			return setState((prevState) => ({
				...prevState,
				step: reservationSteps.calendar,
				lineFilters: [],
				blocksByDay: {},
				dateSelected: null,
				lineSelected: {},
				blockSelected: {},
			}));
		}
		historyBack();
	};

	return children({
		historyBack,
		navToFolder,
		handleOnSelectDate,
		handleOnContinueReservationSteps,
		handleOnBack,
		isActiveLoginModal,
		activeSignUpModalInTemporaryUser,
		setIsRutUpdating,
		setIsRutModalVisible,
		setTemporaryRut,
		setTemporaryDni,
		setWantTemporary,
		ticketAction,
		handleOpenUpdateRutModal,
		closeSignUpModal,
		setOpenRutUpdate,
		handlerWithoutLoginModal,
		setIsVisibleConfirmationModal,
		closeLoginModal,
		isActiveRegistryModal,
		isRutRequired,
		loginModalIsVisible,
		signUpModalIsVisible,
		loading: officeLoading,
		lines:
			step === reservationSteps.showLines ? lineFilters : folders || [],
		linesByFolder,
		lineSelected,
		line: lineSelected,
		blockSelected,
		folder,
		folders,
		category,
		user,
		office,
		hideOfficeDetailText,
		dateSelected,
		reservationSteps,
		step,
		isDniRequired,
		isRutUpdating,
		isRutModalVisible,
		temporaryRut,
		temporaryDni,
		wantTemporary,
	});
};

export default RouteProvider(OfficeProvider);
